import styled from '@emotion/styled';
import { ErrorRounded } from '@mui/icons-material';
import {
  Box,
  Card,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { permissions } from 'src/paths';
import { editSingleCellOfTherapyPlanningAction } from 'src/redux/actions/therapyPlanning';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import classes from '../therapyPlanning/input.module.css';
import {
  ActualDischargeDateIcon,
  AnticipateDischargeDateIcon,
  CellEditedIcon,
  PencilIcon,
  TemplatePasteIcon,
} from './commonComponent/therapy-planning-icons';
import TherapyPlanningTemplate_2_0 from './therapy-planning-template-2.0';
import TherapyPlanningTemplateApply from './therapy-planning-template-apply';
import TherapyPlanningTemplateMenu from './therapy-planning-template-menu';
import { useTherapyPlanning } from './therapyPlanningProvider';
import { therapyPlanningTemplate } from 'src/config';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import DaysLookBackSummary from './7DaysLookBack';
const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '55%',
  borderRadius: '8px',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

export const LightTooltip = styled(
  ({
    className,
    backgroundColor,
    color,
    ...props
  }: TooltipProps & { backgroundColor?: string; color?: string }) => (
    <Tooltip
      {...props}
      classes={{ popper: className }}
    />
  )
)(({ theme, backgroundColor, color }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: backgroundColor,
    color,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: ' rgba(0, 0, 0, 0.24) 0px 3px 8px',
    fontSize: 12,
  },
}));

export const isTherapyFrameStartDate = (evalDate: any, date: any) => {
  const startDate = moment(evalDate);
  const dateToCheck = moment(date);
  const diffDays = dateToCheck.diff(startDate, 'days');

  return diffDays === 0 || (diffDays % 7 === 0 && diffDays >= 0);
};
const isTherapyFrameEndDate = (evalDate: any, date: any) => {
  if (evalDate === date) return false;

  const startDate = moment(evalDate);
  const dateToCheck = moment(date).clone().add(1, 'day');
  if (dateToCheck.format('YYYY-MM-DD') === startDate.format('YYYY-MM-DD')) return false;
  const diffDays = dateToCheck.diff(startDate, 'days');

  return diffDays === 0 || (diffDays % 7 === 0 && diffDays >= 0);
};

const { UPDATE_THERAPIST_PLANNING } = permissions;

type TherapyCard_2_0Type = {
  therapyType: 'PT' | 'OT' | 'SLP';
  uncoveredDay: any;
  actualDischargeDate: any;
  therapyPlanningData_2_0: any;
  dischargeDate: any;
  summaryData: any;
  frequencyStartDate: any;
  dosOfTherapyCard: string | null;
};

const therapyPlanningCardInfo = ['Individual', 'Concurrent', 'Group', '% G&C'];

const TherapyCard_2_0 = (props: TherapyCard_2_0Type) => {
  const {
    therapyType,
    uncoveredDay,
    actualDischargeDate,
    therapyPlanningData_2_0,
    dischargeDate,
    summaryData,
    frequencyStartDate,
    dosOfTherapyCard
  } = props;
  const { weekStartDate } = useTherapyPlanning();
  const [therapyTemplatesEnabled] = useFeatureToggle(therapyPlanningTemplate, 'ENTERPRISE');
  const getTherapistProfileData = useSelector(
    (state: any) => state.getTherapistProfileReducer.response?.data?.currentRoles
  );
  const getMdsAssessmentRange = useSelector((state: any) => state.getMdsAssessmentRangeReducer?.response?.data?.assessmentsByDate)

  const [copyTemplateStartDate, setCopyTemplateStartDate] = useState(null);
  const [openTemplate, setOpenTemplate] = useState(false);
  const [getTherapyTemplateCell, setTherapyTemplateCell] = useState<any>(null);

  const [applyTemplate, setApplyTemplate] = useState(false);
  const handleTemplateCloseApply = () => {
    setTherapyTemplateCell(null);
    setApplyTemplate(false);
  };

  const handleTemplateClose = () => {
    setTherapyTemplateCell(null);
    setOpenTemplate(false);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open: boolean = Boolean(anchorEl);

  const handleCopyTemplate = (
    event: React.MouseEvent<HTMLButtonElement>,
    singleCellItem: singleCellType
  ) => {
    event.stopPropagation();
    setTherapyTemplateCell(singleCellItem);
    setAnchorEl(event.currentTarget);
    // setOpenTemplate(true);
  };

  const handleCloseTemplateMenu = () => {
    setTherapyTemplateCell(null);
    setAnchorEl(null);
  };

  function getLastWeekDate(date: any) {
    return moment(date).subtract(1, 'week').add(1, 'day').format('YYYY-MM-DD');
  }

  const isHoverTemplates = (currentDate: any) => {
    if (!copyTemplateStartDate) return false;
    if (
      currentDate >= copyTemplateStartDate &&
      currentDate <= getNextWeekDate(copyTemplateStartDate)
    )
      return true;
    return false;
  };

  function getNextWeekDate(date: any) {
    return moment(date).add(1, 'week').subtract(1, 'day').format('YYYY-MM-DD');
  }

  const dispatch = useDispatch();
  const providerId = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const selectedPatient = useSelector((state: any) => state?.selectPatientReducer?.patientInfo);
  const editSingleCellResponse = useSelector(
    (state: any) => state.editSingleCellOfTherapyPlanningReducer
  );

  const therapyPlanning_2_0 = useSelector((state: any) => state.getTherapyPlanningDataReducer_2_0);
  const therapyPlanningSummary = useSelector(
    (state: any) => state.getTherapyPlanningSummaryReducer_2_0
  );
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );

  let therapyPlanningEndDate: any = undefined;
  let therapyPlanningStartDate: any = undefined;

  const inputRefs1 = useRef<(HTMLInputElement | null)[]>([]);
  const inputRefs2 = useRef<(HTMLInputElement | null)[]>([]);
  const inputRefs3 = useRef<(HTMLInputElement | null)[]>([]);

  const getNextCellItem = (type: 'individual' | 'concurrent' | 'group', nextIndex: number) => {
    const dataMap = new Map<string, any>([
      ['individual', therapyPlanningData_2_0?.INDIVIDUAL],
      ['concurrent', therapyPlanningData_2_0?.CONCURRENT],
      ['group', therapyPlanningData_2_0?.GROUP],
    ]);

    return dataMap.get(type)?.[nextIndex];
  };

  const disableTheCopyPasteIcon = (dataCell: singleCellType) => {
    if (dataCell?.date >= (actualDischargeDate ?? dischargeDate)) return false;
    return true;
  };

  const getCurrentItemData = (
    type: 'individual' | 'concurrent' | 'group',
    eventType: 'ArrowUp' | 'ArrowDown',
    index: number
  ) => {
    const therapyMapping = new Map([
      [
        'individual',
        new Map([
          ['ArrowUp', null],
          ['ArrowDown', therapyPlanningData_2_0?.CONCURRENT],
        ]),
      ],
      [
        'concurrent',
        new Map([
          ['ArrowUp', therapyPlanningData_2_0?.INDIVIDUAL],
          ['ArrowDown', therapyPlanningData_2_0?.GROUP],
        ]),
      ],
      [
        'group',
        new Map([
          ['ArrowUp', therapyPlanningData_2_0?.CONCURRENT],
          ['ArrowDown', null],
        ]),
      ],
    ]);

    const eventMapping = therapyMapping.get(type)?.get(eventType);
    return eventMapping ? eventMapping[index] : undefined;
  };

  const getNextUpDownType = (
    type: 'individual' | 'concurrent' | 'group',
    eventType: 'ArrowUp' | 'ArrowDown'
  ) => {
    const transitions = new Map<string, Map<string, string | undefined>>([
      [
        'individual',
        new Map([
          ['ArrowUp', undefined],
          ['ArrowDown', 'concurrent'],
        ]),
      ],
      [
        'concurrent',
        new Map([
          ['ArrowUp', 'individual'],
          ['ArrowDown', 'group'],
        ]),
      ],
      [
        'group',
        new Map([
          ['ArrowUp', 'concurrent'],
          ['ArrowDown', undefined],
        ]),
      ],
    ]);

    return transitions.get(type)?.get(eventType);
  };

  const [isHoveredInc, setIsHoveredInc] = useState(false);
  const [isHoveredCon, setIsHoveredCon] = useState(false);
  const [isHoveredGroup, setIsHoveredGroup] = useState(false);
  const [dateToShowIcon, setDateToShowIcon] = useState('');

  const [cellItemData, setCellItemData] = useState<any>('');
  const [isEditableCell, setIsEditableCell] = useState(false);

  const [editingCell, setEditingCell] = useState<{
    date: string;
    index: number;
    type: 'individual' | 'concurrent' | 'group';
  } | null>(null);

  type singleCellType = {
    actualMinutes: string | number;
    date: string;
    discipline: string;
    disciplineId: string;
    isMissedVisit: boolean;
    planMinutes: string | number;
    therapyType: string;
    uuid: string;
  };

  const apiCallToSaveSingleCell = (currentCellData: singleCellType) => {
    if (currentCellData?.planMinutes == cellItemData?.planMinutes) return;
    dispatch(
      editSingleCellOfTherapyPlanningAction({
        patientId: selectedPatient?.uuid,
        providerId: providerId,
        weekStartDate: weekStartDate.format('YYYY-MM-DD'),
        weekEndDate: weekStartDate.clone().add(8, 'days').format('YYYY-MM-DD'),
        params: {
          patientId: selectedPatient?.uuid,
          disciplineId: cellItemData?.uuid ? cellItemData?.uuid : null,
          planMinutes: cellItemData?.planMinutes ? cellItemData?.planMinutes : 0,
          type: cellItemData?.therapyType,
          discipline: therapyType,
          localDate: cellItemData?.date,
        },
      })
    );
  };

  const handleEditCell = (
    e: any,
    individual: any,
    index: number,
    type: 'individual' | 'concurrent' | 'group'
  ) => {
    if (
      type === 'individual' &&
      disablePlanerByDischarge(individual?.date, actualDischargeDate ?? dischargeDate)
    ) {
      return;
    }
    e.stopPropagation();
    setCellItemData(individual);
    setEditingCell({ date: individual.date, index, type });
  };

  const handleBlurCell = (currentCellData: singleCellType) => {
    apiCallToSaveSingleCell(currentCellData);

    setCellItemData('');
    setEditingCell(null);
  };

  const isSummaryCardLoading = (loading: boolean, disciples: any, type: any) => {
    if (loading && disciples === 'ALL') return true;
    if (loading && disciples === type) return true;
    return false;
  };

  const isPlannerDisable = (dataCell: singleCellType) => {
    // return dataCell.date <= '2024-11-29';
    return false;
  };

  const disablePlanerByDischarge = (currentDate: any, dischargeDate: any) => {
    return currentDate > dischargeDate;
  };

  const disablePlanerSummaryByDischarge = (currentDate: any, dischargeDate: any) => {
    return currentDate > dischargeDate;
  };

  const checkIsDosLessSummary = (date: any, DoS: string | null) => {
    if (DoS === null) return true;
    return date < DoS;
  };

  const disablePlannerMin = (currentDate: any, dischargeDate: any) => {
    if (!dischargeDate) return true;
    return currentDate <= dischargeDate;
  };

  const isDisplayTherapyWeek = (dataCell: singleCellType, frequencyStartDate: any) => {
    if (dataCell?.date > (actualDischargeDate ?? dischargeDate)) return false;
    if (!frequencyStartDate?.length) return false;

    return frequencyStartDate?.some((res: any) => {
      return res?.frequencyStartDate === dataCell?.date;
    });
  };

  const isShowFrequencyAlert = (dataCell: singleCellType, frequencyStartDate: any) => {
    if (!frequencyStartDate?.length) return false;

    return frequencyStartDate?.some((res: any) => {
      return res?.frequencyStartDate === dataCell?.date && res?.showFrequencyAlert;
    });
  };

  const hasFrequencyAlert = (data: Array<{ showFrequencyAlert: boolean }>): boolean => {
    if (!data?.length) return false;
    return data?.some((item) => item?.showFrequencyAlert === true);
  };
  const handleChangeInput = (e: any) => {
    if (
      (/^[0-9]*$/.test(e.target.value) &&
        parseInt(e.target.value) >= 0 &&
        parseInt(e.target.value) <= 480) ||
      e.target.value?.length === 0
    )
      setCellItemData({
        ...cellItemData,
        planMinutes: e.target.value,
      });
  };
  const checkIsDosLess = (singleCell: singleCellType, DoS: string | null) => {
    if (DoS === null) return true;
    return singleCell.date < DoS;
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
    list: 'list1' | 'list2' | 'list3',
    currentCellData: singleCellType,
    type: any
  ) => {
    const currentRefs = list === 'list1' ? inputRefs1 : list === 'list2' ? inputRefs2 : inputRefs3;
    const otherRefs = list === 'list1' ? inputRefs2 : inputRefs1;
    if (event.key === 'Enter') {
      apiCallToSaveSingleCell(currentCellData);
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();

      const nextIndex: any = event.key === 'ArrowRight' ? index + 1 : index - 1;
      apiCallToSaveSingleCell(currentCellData);
      setCellItemData(getNextCellItem(type, nextIndex));
      setEditingCell({
        date: getNextCellItem(type, nextIndex)?.date,
        index: nextIndex,
        type,
      });
      setIsEditableCell(true);
      if (nextIndex >= 0 && nextIndex < currentRefs.current.length) {
        currentRefs.current[nextIndex]?.focus();
        // handleBlurCell(individual);
      }
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      let targetRefs: React.MutableRefObject<(HTMLInputElement | null)[]> | null = null;

      if (event.key === 'ArrowUp') {
        if (list === 'list2') {
          targetRefs = inputRefs1;
        } else if (list === 'list3') {
          targetRefs = inputRefs2;
        }
      }

      if (event.key === 'ArrowDown') {
        if (list === 'list1') {
          targetRefs = inputRefs2;
        } else if (list === 'list2') {
          targetRefs = inputRefs3;
        }
      }

      if (getNextUpDownType(type, event.key)) {
        //api call
        apiCallToSaveSingleCell(currentCellData);
        setCellItemData(getCurrentItemData(type, event.key, index));
        setEditingCell({
          date: getCurrentItemData(type, event.key, index)?.date,
          index: index,
          type: getNextUpDownType(type, event.key) as any,
        });
        setIsEditableCell(true);
      }

      if (targetRefs) {
        targetRefs.current[index]?.focus();
      }
    }
  };

  const isPastDates = (item: any) => {
    return item?.date < moment().format('YYYY-MM-DD')
  }

  const renderFrequency = (frequencyStartDate?: { frequencyType?: string; frequencyValue?: number }[]) => {
    const { frequencyType, frequencyValue = 0 } = frequencyStartDate?.[0] ?? {};

    return ["Visit", "Visits"]?.includes(frequencyType || "")
      ? `${frequencyType}: ${frequencyValue ?? '0'}`
      : frequencyType === 'Week' ? `${frequencyValue ?? '0'} /wk`
        : null;
  };

  const toDisplayReasonForGrayOutForDis = (individual: any) => {
    const lastDateCurrentFrame = therapyPlanningData_2_0?.INDIVIDUAL?.[therapyPlanningData_2_0?.INDIVIDUAL.length - 1];
    return lastDateCurrentFrame?.date === individual?.date
  }

  const toDisplayReasonForGrayOutForCert = (individual: any, dosOfTherapyCard: any) => {
    const lastDateCurrentFrame = therapyPlanningData_2_0?.INDIVIDUAL?.[therapyPlanningData_2_0?.INDIVIDUAL.length - 1]?.date;
    const firstDateCurrentFrame = therapyPlanningData_2_0?.INDIVIDUAL?.[0]?.date;

    let resultDate: string | null = null;
    if (
      dosOfTherapyCard &&
      firstDateCurrentFrame &&
      lastDateCurrentFrame &&
      new Date(dosOfTherapyCard) >= new Date(firstDateCurrentFrame) &&
      new Date(dosOfTherapyCard) <= new Date(lastDateCurrentFrame)
    ) {
      const oneDayBefore = new Date(dosOfTherapyCard);
      oneDayBefore.setDate(oneDayBefore.getDate() - 1);
      resultDate = oneDayBefore.toISOString().split('T')[0];
    }

    return resultDate === individual?.date || lastDateCurrentFrame === individual?.date
  }


  return (
    <>
      <Card
        sx={{
          my: 1,
          borderBottom: `4px solid ${therapyType === 'OT' ? '#B1B2F8' : therapyType === 'PT' ? '#8A8CF5' : '#6366F1'
            }`,
        }}
      >
        <Stack
          sx={{ height: '225px' }}
          flexDirection="row"
        >
          <Stack
            justifyContent="center"
            alignItems="center"
            flex=".5"
            sx={{
              borderRight: '1px solid #E5E7EB',
              background:
                therapyType === 'OT' ? '#B1B2F8' : therapyType === 'PT' ? '#8A8CF5' : '#6366F1',
              fontSize: '12px',
              fontWeight: '500',
              color: '#111927',
            }}
          >
            <Stack alignItems="center">
              <Typography
                variant="body1"
                sx={{ color: '#fff', fontSize: '14px', fontWeight: 700 }}
              >
                {therapyType}
              </Typography>
            </Stack>
            <Stack
              sx={{
                width: '59px',
                height: '24px',
                borderRadius: '6px',
                border: `0.2px solid ${
                  hasFrequencyAlert(frequencyStartDate) ? '#E41000' : '#6366F1'
                }`,
                background: 'rgba(193, 194, 255, 0.60)',
                color: '#FFF',
              }}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'center'}
              gap={0.5}
            >
              {hasFrequencyAlert(frequencyStartDate) && (
                <LightTooltip
                  placement="top"
                  backgroundColor={'#B42318'}
                  color={'white'}
                  title={'Frequency alert'}
                >
                  <ErrorRounded
                    sx={{
                      width: '15px',
                      height: '15px',
                      color: '#F71504',
                      cursor: 'pointer',
                    }}
                  />
                </LightTooltip>
              )}

              <Typography
                sx={{
                  fontSize: '11px',
                  color: '#fff',
                  fontWeight: 600,
                }}
              >
                {frequencyStartDate?.[0]?.frequencyValue ?? '0'}/wk{' '}
              </Typography>
            </Stack>
          </Stack>

          <Stack flex="9.5">
            <Stack
              sx={{ width: '100%' }}
              flexDirection="row"
              flex={7.5}
            >
              <Stack
                sx={{
                  height: '100%',
                  borderRight: '1px solid #E5E7EB',
                  backgroundColor: '#F8F9FA',
                  maxWidth: '128px',
                  minWidth: '83px',
                }}
                flex=".982"
              >
                {therapyPlanningCardInfo?.map((info: string) => {
                  return (
                    <Stack
                      key={info}
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        height: '33.3%',
                        fontWeight: '500',
                        fontSize: '12px',
                        color: '#6C737F',
                      }}
                    >
                      {info}
                    </Stack>
                  );
                })}
              </Stack>
              <Stack sx={{ flex: '8.5' }}>
                {!therapyPlanning_2_0?.loading && (
                  <Stack
                    flexDirection="row"
                    sx={{ width: '100%', height: '25%' }}
                  >
                    {therapyPlanningData_2_0?.INDIVIDUAL?.map((individual: any, index: any) => {
                      if (
                        isTherapyFrameEndDate(
                          frequencyStartDate?.[0]?.dateOfService ?? null,
                          individual?.date
                        )
                      )
                        therapyPlanningEndDate = individual?.date;
                      if (
                        isTherapyFrameStartDate(
                          frequencyStartDate?.[0]?.dateOfService ?? null,
                          individual?.date
                        )
                      )
                        therapyPlanningStartDate = individual?.date;

                      return (
                        <LightTooltip
                          placement="top"
                          backgroundColor={''}
                          color={''}
                          title={''}
                          key={index}
                        >
                          <Stack
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                              width: `calc(100% / 7)`,
                              position: 'relative',
                              borderLeft: isDisplayTherapyWeek(individual, frequencyStartDate)
                                ? `6px solid ${isShowFrequencyAlert(individual, frequencyStartDate)
                                  ? '#E41000'
                                  : '#6750A4'
                                }`
                                : 'none',

                              fontSize: '14px',
                              fontWeight: '400',
                              color: '#6C737F',
                              cursor: 'pointer',
                              backgroundColor: isHoverTemplates(individual?.date)
                                ? '#EBEEFE'
                                : (uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay)
                                  || getMdsAssessmentRange?.[individual?.date]
                                  ? '#F2F4F7'
                                  : isPlannerDisable(individual) ||
                                    disablePlanerByDischarge(
                                      individual?.date,
                                      actualDischargeDate ?? dischargeDate
                                    ) ||
                                    checkIsDosLess(individual, dosOfTherapyCard) ||
                                    !permissionData?.includes(UPDATE_THERAPIST_PLANNING)
                                    ? 'rgba(56, 66, 80, 0.08)'
                                    : individual?.date === moment().format('YYYY-MM-DD')
                                      ? '#EBEEFE'
                                      : 'none',
                              pointerEvents:
                                isPlannerDisable(individual) ||
                                  isPastDates(individual) ||
                                  checkIsDosLess(individual, dosOfTherapyCard) ||
                                  !permissionData?.includes(UPDATE_THERAPIST_PLANNING)
                                  ? 'none'
                                  : 'auto',

                            }}
                            onMouseEnter={() => {
                              setIsHoveredInc(true);
                              setDateToShowIcon(individual?.date);
                            }}
                            onMouseLeave={() => {
                              setIsHoveredInc(false);
                              setDateToShowIcon('');
                            }}
                            onClick={(e) => handleEditCell(e, individual, index, 'individual')}
                          >

                            {individual?.date === dischargeDate && (
                              <Tooltip
                                placement="bottom"
                                title={
                                  'Anticipated Discharge'
                                }
                              >
                                <Box
                                  sx={{
                                    padding: '8px',
                                    position: 'absolute',
                                    left: '4px',
                                    boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: 'white',
                                    justifyContent: 'center',
                                    width: '32px',
                                    zIndex: 9,
                                    height: '32px',
                                  }}
                                >
                                  {dischargeDate === individual?.date && (
                                    <AnticipateDischargeDateIcon />
                                  )
                                  }
                                </Box>
                              </Tooltip>
                            )}
                            {individual?.date === actualDischargeDate && (
                              <Tooltip
                                placement="bottom"
                                title={
                                  'Discharge'
                                }
                              >
                                <Box
                                  sx={{
                                    padding: '8px',
                                    position: 'absolute',
                                    left: '4px',
                                    boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: 'white',
                                    justifyContent: 'center',
                                    width: '32px',
                                    zIndex: 9,
                                    height: '32px',
                                  }}
                                >
                                  {actualDischargeDate === individual?.date && (
                                    <ActualDischargeDateIcon />
                                  )
                                  }

                                </Box>
                              </Tooltip>
                            )}
                            {disablePlannerMin(
                              individual?.date,
                              actualDischargeDate ?? dischargeDate
                            ) ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '-webkit-fill-available',
                                  justifyContent:
                                    isHoveredInc &&
                                      dateToShowIcon === individual?.date &&
                                      editingCell?.date !== individual?.date
                                      ? 'normal'
                                      : 'center',
                                    gap: 0.2,
                                  height: '100%',

                                }}
                              >
                                {isHoveredInc &&
                                    dateToShowIcon === individual?.date &&
                                    editingCell?.date !== individual?.date ? (
                                    <IconButton
                                    size="small"
                                  >
                                    <PencilIcon />
                                  </IconButton>
                                ) : editSingleCellResponse?.isCellEdited &&
                                  editSingleCellResponse?.editedCellPayload?.params?.startDate ===
                                      individual?.date &&
                                  editSingleCellResponse?.editedCellPayload?.therapyType ===
                                      therapyType &&
                                  editSingleCellResponse?.editedCellPayload?.groupType ===
                                      individual?.therapyType ? (
                                  <IconButton
                                    sx={{
                                      position: 'relative',
                                      margin: '-20px',
                                    }}
                                  >
                                    <CellEditedIcon />
                                  </IconButton>
                                ) : null}

                                {editingCell?.date === individual?.date &&
                                    editingCell?.type === 'individual' ? (
                                  <Box
                                    sx={{
                                      backgroundColor: 'white',
                                      width: '70px',

                                      height: '28px',
                                      borderRadius: '8px',
                                      border: '0.5px solid #6366F1',
                                      background: '#FFF',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <input
                                      type="text"
                                      value={cellItemData?.planMinutes || ''}
                                      onBlur={() => handleBlurCell(individual)}
                                      key={`list1-${index}`}
                                      onKeyDown={(e) =>
                                        handleKeyDown(e, index, 'list1', individual, 'individual')
                                      }
                                      ref={(el) => (inputRefs1.current[index] = el)}
                                      onChange={(e) => handleChangeInput(e)}
                                      style={{
                                        borderWidth: '0px',
                                        width: '40%',
                                        backgroundColor:
                                          uncoveredDay?.length === 9 &&
                                            uncoveredDay[index]?.isUncoveredDay
                                            ? '#F2F4F7'
                                            : 'white',
                                        height: '20px',
                                        textAlign: 'center',
                                        outline: 'none',
                                        borderRadius: '8px',
                                        borderLeft: 'none',
                                      }}
                                      className={classes.input}
                                      autoFocus
                                    />
                                    <Divider
                                      orientation="vertical"
                                      sx={{
                                        color: '#464646',
                                        height: '18px',
                                        width: '5px',
                                        borderColor: '#6366F1',
                                      }}
                                    />
                                    <input
                                      type="text"
                                      disabled
                                      value={
                                        individual?.isMissedVisit
                                          ? 'X'
                                          : individual?.actualMinutes
                                            ? individual?.actualMinutes
                                            : 0
                                      }
                                      style={{
                                        borderWidth: '0px',
                                        width: '40%',
                                        backgroundColor:
                                          uncoveredDay?.length === 9 &&
                                            uncoveredDay[index]?.isUncoveredDay
                                            ? '#F2F4F7'
                                            : 'white',
                                        height: '20px',
                                        textAlign: 'center',
                                        outline: 'none',
                                        borderRadius: '8px',
                                      }}
                                      className={classes.input}
                                    />
                                  </Box>
                                  ) : checkIsDosLess(individual, dosOfTherapyCard) ? <>
                                      {/* {toDisplayReasonForGrayOutForCert(individual, dosOfTherapyCard) ?
                                      <Box sx={{
                                        borderRadius: '0px 12px',
                                        background: '#FFF',
                                          boxShadow: '4px 7px 12px 4px rgba(0, 0, 0, 0.05)',
                                        padding: '5px',
                                          margin: '12px',
                                          zIndex: 1,
                                          marginTop: '42px',
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          textAlign: 'center',

                                      }} >
                                          <Typography variant='caption' >
                                            No Active Certification
                                        </Typography>
                                      </Box> : null
                                    } */}
                                      {null}

                                  </> : (
                                  <Box
                                    sx={{
                                      position: 'relative',
                                    }}
                                      >{`${individual?.planMinutes ? individual?.planMinutes : '--'
                                        } | ${individual?.isMissedVisit
                                      ? 'X'
                                      : individual?.actualMinutes
                                              ? individual?.actualMinutes
                                              : '--'
                                          }`}</Box>
                                )}
                              </Box>
                            ) : toDisplayReasonForGrayOutForDis(individual) ?
                              <>
                                  {/* <Box sx={{
                                  borderRadius: '0px 12px',
                                  background: '#FFF',
                                    boxShadow: '4px 7px 12px 4px rgba(0, 0, 0, 0.05)',
                                  padding: '5px',
                                    margin: '12px',
                                    zIndex: 1,
                                    marginTop: '42px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    textAlign: 'center',

                                }} >
                                    <Typography variant='caption' >
                                      {
                                        actualDischargeDate && actualDischargeDate <= individual?.date ? 'No Active Certification' : 'Anticipated Discharge Set'
                                      }
                                  </Typography>
                                </Box> */}
                                  {
                                    null
                                  }
                              </> : null}
                          </Stack>
                        </LightTooltip>
                      );
                    })}
                  </Stack>
                )}

                {!therapyPlanning_2_0?.loading && (
                  <Stack
                    flexDirection="row"
                    sx={{ width: '100%', height: '25%' }}
                  >
                    {therapyPlanningData_2_0?.CONCURRENT?.map((concurrent: any, index: any) => (
                      <LightTooltip
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, 48],
                                },
                              },
                            ],
                          },
                        }}
                        backgroundColor={''}
                        color={''}
                        placement="top"
                        title={''}
                        key={index}
                      >
                        <Stack
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            width: `calc(100% / 7)`,
                            fontSize: '14px',
                            position: 'relative',
                            fontWeight: '400',
                            borderLeft: isDisplayTherapyWeek(concurrent, frequencyStartDate)
                              ? `6px solid ${isShowFrequencyAlert(concurrent, frequencyStartDate)
                                ? '#E41000'
                                : '#6750A4'
                              }`
                              : 'none',
                            color: '#6C737F',
                            cursor: 'pointer',
                            backgroundColor: isHoverTemplates(concurrent?.date)
                              ? '#EBEEFE'
                              : (uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay)
                                || getMdsAssessmentRange?.[concurrent?.date]
                                ? '#F2F4F7'
                                : isPlannerDisable(concurrent) ||
                                  disablePlanerByDischarge(
                                    concurrent?.date,
                                    actualDischargeDate ?? dischargeDate
                                  ) ||
                                  checkIsDosLess(concurrent, dosOfTherapyCard) ||
                                  !permissionData?.includes(UPDATE_THERAPIST_PLANNING)
                                  ? 'rgba(56, 66, 80, 0.08)'
                                  : concurrent?.date === moment().format('YYYY-MM-DD')
                                    ? '#EBEEFE'
                                    : 'none',
                            pointerEvents:
                              isPlannerDisable(concurrent) ||
                                disablePlanerByDischarge(
                                  concurrent?.date,
                                  actualDischargeDate ?? dischargeDate
                                ) || isPastDates(concurrent) ||
                                checkIsDosLess(concurrent, dosOfTherapyCard) ||
                                !permissionData?.includes(UPDATE_THERAPIST_PLANNING)
                                ? 'none'
                                : 'auto',
                          }}
                          onMouseEnter={() => {
                            setIsHoveredCon(true);
                            setDateToShowIcon(concurrent?.date);
                          }}
                          onMouseLeave={() => {
                            setIsHoveredCon(false);
                            setDateToShowIcon('');
                          }}
                          onClick={(e) => handleEditCell(e, concurrent, index, 'concurrent')}
                        >
                          {isDisplayTherapyWeek(concurrent, frequencyStartDate) ? (
                            <Box
                              sx={{
                                position: 'absolute',
                                left: '-4px',

                                width: '12px',
                                height: '16px',
                                clipPath: 'polygon(0% 0%,100% 46%,100% 54%, 0% 100%)',
                                backgroundColor: isShowFrequencyAlert(
                                  concurrent,
                                  frequencyStartDate
                                )
                                  ? '#E41000'
                                  : '#6750A4',
                              }}
                            />
                          ) : null}
                          {disablePlannerMin(
                            concurrent?.date,
                            actualDischargeDate ?? dischargeDate
                          ) ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '-webkit-fill-available',
                                justifyContent:
                                  isHoveredCon &&
                                    dateToShowIcon === concurrent?.date &&
                                    editingCell?.date !== concurrent?.date
                                    ? 'normal'
                                    : 'center',
                                gap: 0.2,
                              }}
                            >
                              {isHoveredCon &&
                                  dateToShowIcon === concurrent?.date &&
                                  editingCell?.date !== concurrent?.date ? (
                                <IconButton size="small">
                                  <PencilIcon />
                                </IconButton>
                              ) : editSingleCellResponse?.isCellEdited &&
                                editSingleCellResponse?.editedCellPayload?.params?.startDate ===
                                    concurrent?.date &&
                                editSingleCellResponse?.editedCellPayload?.therapyType ===
                                    therapyType &&
                                editSingleCellResponse?.editedCellPayload?.groupType ===
                                    concurrent?.therapyType ? (
                                <IconButton
                                  sx={{
                                    position: 'relative',
                                    margin: '-20px',
                                  }}
                                >
                                  <CellEditedIcon />
                                </IconButton>
                              ) : null}

                              {editingCell?.date === concurrent?.date &&
                                  editingCell?.type === 'concurrent' ? (
                                <Box
                                  sx={{
                                    backgroundColor: 'white',

                                    width: '70px',

                                    height: '28px',
                                    borderRadius: '8px',
                                    border: '0.5px solid #6366F1',
                                    background: '#FFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <input
                                    type="text"
                                    value={cellItemData?.planMinutes || ''}
                                    onBlur={() => handleBlurCell(concurrent)}
                                    onChange={(e) => handleChangeInput(e)}
                                    key={`list2-${index}`}
                                    onKeyDown={(e) =>
                                      handleKeyDown(e, index, 'list1', concurrent, 'concurrent')
                                    }
                                    ref={(el) => (inputRefs2.current[index] = el)}
                                    style={{
                                      borderWidth: '0px',
                                      width: '40%',
                                      backgroundColor:
                                        uncoveredDay?.length === 9 &&
                                          uncoveredDay[index]?.isUncoveredDay
                                          ? '#F2F4F7'
                                          : 'white',
                                      height: '20px',
                                      textAlign: 'center',
                                      outline: 'none',
                                      borderRadius: '8px',
                                      borderLeft: 'none',
                                    }}
                                    className={classes.input}
                                    autoFocus
                                  />
                                  <Divider
                                    orientation="vertical"
                                    sx={{
                                      color: '#464646',
                                      height: '18px',
                                      width: '5px',
                                      borderColor: '#6366F1',
                                    }}
                                  />
                                  <input
                                    type="text"
                                    disabled
                                    value={
                                      concurrent?.isMissedVisit
                                        ? 'X'
                                        : concurrent?.actualMinutes
                                          ? concurrent?.actualMinutes
                                          : 0
                                    }
                                    style={{
                                      borderWidth: '0px',
                                      width: '40%',
                                      backgroundColor:
                                        uncoveredDay?.length === 9 &&
                                          uncoveredDay[index]?.isUncoveredDay
                                          ? '#F2F4F7'
                                          : 'white',
                                      height: '20px',
                                      textAlign: 'center',
                                      outline: 'none',
                                      borderRadius: '8px',
                                    }}
                                    className={classes.input}
                                  />
                                </Box>
                              ) : checkIsDosLess(concurrent, dosOfTherapyCard) ? null : (
                                <Box
                                  sx={{
                                    position: 'relative',
                                  }}
                                    >{`${concurrent?.planMinutes ? concurrent?.planMinutes : '--'} | ${concurrent?.isMissedVisit
                                      ? 'X'
                                      : concurrent?.actualMinutes
                                    ? concurrent?.actualMinutes
                                    : '--'
                                        }`}</Box>
                              )}
                            </Box>
                          ) : null}
                        </Stack>
                      </LightTooltip>
                    ))}
                  </Stack>
                )}

                {!therapyPlanning_2_0?.loading && (
                  <Stack
                    flexDirection="row"
                    sx={{ width: '100%', height: '25%' }}
                  >
                    {therapyPlanningData_2_0?.GROUP?.map((group: any, index: any) => {
                      if (
                        isTherapyFrameStartDate(
                          frequencyStartDate?.[0]?.dateOfService ?? null,
                          group?.date
                        )
                      )
                        therapyPlanningStartDate = group?.date;

                      return (
                        <LightTooltip
                          key={index}
                          backgroundColor={''}
                          color={''}
                          placement="top"
                          title={''}
                        >
                          <Stack
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                              width: `calc(100% / 7)`,
                              position: 'relative',
                              fontSize: '14px',
                              fontWeight: '400',
                              borderLeft: isDisplayTherapyWeek(group, frequencyStartDate)
                                ? `6px solid ${isShowFrequencyAlert(group, frequencyStartDate)
                                  ? '#E41000'
                                  : '#6750A4'
                                }`
                                : 'none',
                              color: '#6C737F',
                              cursor: 'pointer',
                              backgroundColor: isHoverTemplates(group?.date)
                                ? '#EBEEFE'
                                : (uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay)
                                  || getMdsAssessmentRange?.[group?.date]
                                  ? '#F2F4F7'
                                  : isPlannerDisable(group) ||
                                    disablePlanerByDischarge(
                                      group?.date,
                                      actualDischargeDate ?? dischargeDate
                                    ) ||
                                    checkIsDosLess(group, dosOfTherapyCard) ||
                                    !permissionData?.includes(UPDATE_THERAPIST_PLANNING)
                                    ? 'rgba(56, 66, 80, 0.08)'
                                    : group?.date === moment().format('YYYY-MM-DD')
                                      ? '#EBEEFE'
                                      : 'none',
                              pointerEvents:
                                isPlannerDisable(group) ||
                                  disablePlanerByDischarge(
                                    group?.date,
                                    actualDischargeDate ?? dischargeDate
                                  ) || isPastDates(group) ||
                                  checkIsDosLess(group, dosOfTherapyCard) ||
                                  !permissionData?.includes(UPDATE_THERAPIST_PLANNING)
                                  ? 'none'
                                  : 'auto',
                            }}
                            onMouseEnter={() => {
                              setIsHoveredGroup(true);
                              setDateToShowIcon(group?.date);
                            }}
                            onMouseLeave={() => {
                              setIsHoveredGroup(false);
                              setDateToShowIcon('');
                            }}
                            onClick={(e) => handleEditCell(e, group, index, 'group')}
                          >
                            {therapyPlanningStartDate === group?.date &&
                              getTherapistProfileData?.includes('dor') &&
                              disableTheCopyPasteIcon(group) &&
                              therapyTemplatesEnabled &&
                              (
                                <Tooltip
                                  placement="bottom-start"
                                  title="Templates"
                                >
                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      padding: { md: '8px', xs: '5px' },
                                      borderRadius: '0px 4px 4px 0px',
                                      background: 'rgba(99, 102, 241, 0.50)',
                                      '&:hover': {
                                        background: 'rgba(99, 102, 241, 0.50)',
                                      },
                                      left: '0px',
                                      color: 'white',
                                      top: { xs: '14px', md: '10px' },
                                      zIndex: 10,
                                    pointerEvents: 'auto'
                                    }}
                                    onMouseEnter={() => setCopyTemplateStartDate(group?.date)}
                                    onMouseLeave={() => {
                                      setCopyTemplateStartDate(null);
                                    }}
                                    onClick={(event) => handleCopyTemplate(event, group)}
                                  >
                                    <TemplatePasteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            {disablePlannerMin(
                              group?.date,
                              actualDischargeDate ?? dischargeDate
                            ) ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '-webkit-fill-available',
                                  justifyContent:
                                    isHoveredGroup &&
                                      dateToShowIcon === group?.date &&
                                      editingCell?.date !== group?.date
                                      ? 'normal'
                                      : 'center',
                                  gap: 0.2,
                                }}
                              >
                                {isHoveredGroup &&
                                    dateToShowIcon === group?.date &&
                                    editingCell?.date !== group?.date ? (
                                  <IconButton size="small">
                                    <PencilIcon />
                                  </IconButton>
                                ) : editSingleCellResponse?.isCellEdited &&
                                  editSingleCellResponse?.editedCellPayload?.params?.startDate ===
                                      group?.date &&
                                  editSingleCellResponse?.editedCellPayload?.therapyType ===
                                      therapyType &&
                                  editSingleCellResponse?.editedCellPayload?.groupType ===
                                      group?.therapyType ? (
                                  <IconButton
                                    sx={{
                                      position: 'relative',
                                      margin: '-20px',
                                    }}
                                  >
                                    <CellEditedIcon />
                                  </IconButton>
                                ) : null}

                                {editingCell?.date === group?.date &&
                                    editingCell?.type === 'group' ? (
                                  <Box
                                    sx={{
                                      backgroundColor: 'white',
                                      width: '70px',
                                      height: '28px',
                                      borderRadius: '8px',
                                      border: '0.5px solid #6366F1',
                                      background: '#FFF',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <input
                                      type="text"
                                      value={cellItemData?.planMinutes || ''}
                                      onBlur={() => handleBlurCell(group)}
                                      key={`list3-${index}`}
                                      onKeyDown={(e) =>
                                        handleKeyDown(e, index, 'list3', group, 'group')
                                      }
                                      ref={(el) => (inputRefs3.current[index] = el)}
                                      onChange={(e) => handleChangeInput(e)}
                                      style={{
                                        borderWidth: '0px',
                                        width: '40%',
                                        backgroundColor:
                                          uncoveredDay?.length === 9 &&
                                            uncoveredDay[index]?.isUncoveredDay
                                            ? '#F2F4F7'
                                            : 'white',
                                        height: '20px',
                                        textAlign: 'center',
                                        outline: 'none',
                                        borderRadius: '8px',
                                        borderLeft: 'none',
                                      }}
                                      className={classes.input}
                                      autoFocus
                                    />
                                    <Divider
                                      orientation="vertical"
                                      sx={{
                                        color: '#464646',
                                        height: '18px',
                                        width: '5px',
                                        borderColor: '#6366F1',
                                      }}
                                    />
                                    <input
                                      type="text"
                                      disabled
                                      value={
                                        group?.isMissedVisit
                                          ? 'X'
                                          : group?.actualMinutes
                                            ? group?.actualMinutes
                                            : 0
                                      }
                                      style={{
                                        borderWidth: '0px',
                                        width: '40%',
                                        backgroundColor:
                                          uncoveredDay?.length === 9 &&
                                            uncoveredDay[index]?.isUncoveredDay
                                            ? '#F2F4F7'
                                            : 'white',
                                        height: '20px',
                                        textAlign: 'center',
                                        outline: 'none',
                                        borderRadius: '8px',
                                      }}
                                      className={classes.input}
                                    />
                                  </Box>
                                ) : checkIsDosLess(group, dosOfTherapyCard) ? null : (
                                  <Box
                                    sx={{
                                      position: 'relative',
                                    }}
                                      >{`${group?.planMinutes ? group?.planMinutes : '--'} | ${group?.isMissedVisit
                                        ? 'X'
                                        : group?.actualMinutes
                                      ? group?.actualMinutes
                                      : '--'
                                          }`}</Box>
                                )}
                              </Box>
                            ) : null}
                          </Stack>
                        </LightTooltip>
                      );
                    })}
                  </Stack>
                )}

                {therapyPlanning_2_0?.loading && (
                  <Stack
                    sx={{ padding: '.5rem' }}
                    gap="1.2rem"
                  >
                    <Skeleton
                      height={25}
                      variant="rounded"
                    />
                    <Skeleton
                      variant="rounded"
                      height={25}
                      animation="wave"
                    />
                    <Skeleton
                      variant="rounded"
                      height={25}
                    />
                  </Stack>
                )}
                {isSummaryCardLoading(
                  therapyPlanningSummary?.loading,
                  therapyPlanningSummary?.requestDiscipline,
                  therapyType
                ) ? (
                  <Stack
                    sx={{ padding: '.5rem' }}
                    gap="1.2rem"
                  >
                    <Skeleton
                      variant="rounded"
                      height={25}
                    />
                  </Stack>
                ) : (
                  <Stack
                    flexDirection="row"
                      sx={{
                        height: '25%',
                        background: '#F8F9FA'
                      }}
                  >
                    {summaryData?.map((summary: any, index: any) => (
                      <LightTooltip
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, 48],
                                },
                              },
                            ],
                          },
                        }}
                        backgroundColor={''}
                        color={''}
                        placement="top"
                        title=""
                        key={index}
                      >
                        <Stack
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            width: `calc(100% / 7)`,
                            fontSize: '14px',
                            position: 'relative',
                            fontWeight: '400',

                            color: summary?.summary >= 25 ? '#E07B00' : '#6C737F',
                          }}
                        >
                          {disablePlanerSummaryByDischarge(
                            summary?.date,
                            actualDischargeDate ?? dischargeDate
                          ) ||
                            checkIsDosLessSummary(
                              summary?.date,
                              dosOfTherapyCard
                            ) ? null : summary?.summary ? (
                              <> {summary?.summary}%</>
                            ) : null}
                        </Stack>
                      </LightTooltip>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Card>
      <BootstrapDialog
        onClose={handleTemplateClose}
        aria-labelledby="customized-dialog-title"
        open={openTemplate}
      >
        <TherapyPlanningTemplate_2_0
          close={handleTemplateClose}
          therapyType={therapyType}
          getTherapyTemplateCell={getTherapyTemplateCell}
          dischargeDate={actualDischargeDate ?? dischargeDate}
        />
      </BootstrapDialog>

      <BootstrapDialog
        onClose={handleTemplateCloseApply}
        aria-labelledby="customized-dialog-title"
        open={applyTemplate}
      >
        <TherapyPlanningTemplateApply
          close={handleTemplateCloseApply}
          therapyType={therapyType}
          getTherapyTemplateCell={getTherapyTemplateCell}
          dischargeDate={actualDischargeDate ?? dischargeDate}
        />
      </BootstrapDialog>

      <TherapyPlanningTemplateMenu
        anchorEl={anchorEl}
        handleCloseTemplateMenu={handleCloseTemplateMenu}
        open={open}
        setOpenTemplate={setOpenTemplate}
        setApplyTemplate={setApplyTemplate}
        setAnchorEl={setAnchorEl}
      />
    </>
  );
};

export default React.memo(TherapyCard_2_0);
